const emitProfileCreationStatus = (onStatus, event) => {
  if (typeof onStatus !== 'function') return;

  try {
    onStatus(event);
  } catch (error) {
    console.warn('[profileCreation] Status callback failed:', error);
  }
};

export const isMissingProfileColumnError = (error) => {
  if (!error) return false;

  const code = String(error.code || '').toUpperCase();
  const message = [
    error.message,
    error.details,
    error.hint,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const mentionsOnboardingColumn =
    message.includes('onboarding_context') ||
    message.includes('onboarding_completed_at');

  const looksLikeMissingColumnError =
    message.includes('does not exist') ||
    message.includes('schema cache') ||
    message.includes('could not find');

  return (
    mentionsOnboardingColumn &&
    (looksLikeMissingColumnError || code === '42703' || code === 'PGRST204')
  );
};

const upsertProfile = async ({ supabaseClient, payload }) => (
  supabaseClient
    .from('profiles')
    .upsert(payload, {
      onConflict: 'id',
      ignoreDuplicates: true
    })
    .select()
    .maybeSingle()
);

export const createProfileIfMissing = async ({ supabaseClient, userId, email, onStatus }) => {
  if (!supabaseClient || !userId || !email) return null;

  const baseProfileData = {
    id: userId,
    email,
    subscription_type: 'free',
    subscription_start_date: new Date().toISOString(),
    onboarding_completed: false,
    subscription_expires_at: null,
    downsell_redeemed_at: null
  };

  const extendedOnboardingFields = {
    onboarding_context: {},
    onboarding_completed_at: null
  };

  let insertedProfile = null;

  try {
    emitProfileCreationStatus(onStatus, {
      stage: 'create_attempt_started',
      payload: 'full'
    });

    let { data: fullProfile, error: fullInsertError } = await upsertProfile({
      supabaseClient,
      payload: {
        ...baseProfileData,
        ...extendedOnboardingFields
      }
    });

    insertedProfile = fullProfile ?? null;

    if (fullInsertError && isMissingProfileColumnError(fullInsertError)) {
      console.warn(
        '[profileCreation] Missing onboarding columns during profile bootstrap, retrying with legacy payload:',
        fullInsertError
      );
      emitProfileCreationStatus(onStatus, {
        stage: 'missing_columns_retry',
        error: fullInsertError
      });

      const { data: fallbackProfile, error: fallbackInsertError } = await upsertProfile({
        supabaseClient,
        payload: baseProfileData
      });

      insertedProfile = fallbackProfile ?? insertedProfile;

      if (fallbackInsertError) {
        console.error('[profileCreation] Fallback profile insert failed:', fallbackInsertError);
        emitProfileCreationStatus(onStatus, {
          stage: 'fallback_insert_failed',
          error: fallbackInsertError
        });
      }
    } else if (fullInsertError) {
      console.error('[profileCreation] Full profile insert failed:', fullInsertError);
      emitProfileCreationStatus(onStatus, {
        stage: 'initial_insert_failed',
        error: fullInsertError
      });
    }

    // Only refetch from DB when upsert didn't return data (e.g. ignoreDuplicates row already existed)
    let resolvedProfile = insertedProfile;
    if (!resolvedProfile) {
      const { data: existingProfile, error: fetchError } = await supabaseClient
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (fetchError) {
        console.error('[profileCreation] Error fetching profile after creation attempt:', fetchError);
        emitProfileCreationStatus(onStatus, {
          stage: 'profile_creation_failed',
          error: fetchError
        });
        return null;
      }
      resolvedProfile = existingProfile;
    }

    if (!resolvedProfile) {
      emitProfileCreationStatus(onStatus, {
        stage: 'profile_creation_failed'
      });
      return null;
    }

    emitProfileCreationStatus(onStatus, {
      stage: 'profile_ready',
      source: insertedProfile ? 'insert' : 'refetch'
    });

    return resolvedProfile;
  } catch (error) {
    console.error('[profileCreation] Exception during profile creation:', error);
    emitProfileCreationStatus(onStatus, {
      stage: 'profile_creation_failed',
      error
    });
    return null;
  }
};
